<template>
  <k-panel title="Line Manager"
    :showContent="showContent"
    @toggle="showContent = $event"
  >
    <template #body>
        <div>
          <div class="line-manager-display">
            <div v-if="lineManager">
              <span>
                <router-link
                  v-if="$permissions.hasPermission('view_all_learners')"
                  :to="{ name: 'dashboard_students', params: { studentId: lineManager.user_id } }"
                >
                  <b>{{ lineManager.name }}</b><i class="fas fa-external-link-alt" aria-hidden="true"></i>
                </router-link>
                <span v-else>
                  <b>{{ lineManager.name }}</b>
                </span>
              </span>
              <p>{{ lineManager.email }}</p>
            </div>
            <div v-else>
              <span>No line manager assigned</span>
            </div>
            <div v-if="canUpdate" class="line-manager-actions">
              <button class="btn btn-primary" @click="showEditModal = true">
                Edit <i class="fas fa-edit" aria-hidden="true"></i>
              </button>
              <button v-if="lineManager" class="btn btn-danger" @click="showRemoveModal = true">
                Remove <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <k-modal
            v-if="showEditModal"
            :show="showEditModal"
            @close="cancelEdit"
            class="update-line-manager-modal"
          >
            <template #header>
              <h3>Update Line Manager</h3>
            </template>
            <template #body>
              <k-nav-tabs
                :tabs="tabs"
                :current-tab="activeTab"
                :use-routes="false"
                @tab-selected="activeTab = $event"
              />
              <div class="modal-content-wrapper">
                <div v-if="activeTab === 'assign-existing'">
                  <div class="assign-existing-manager">
                    <p>Assign a Line Manager who already has an account on KATE to this learner.</p>
                    <div class="user-search-container">
                      <label for="user-search-input">
                        <k-text-search
                          id="user-search-input"
                          v-model="filterText"
                          @keyup.enter="userSearch"
                          input-placeholder="Search for line manager"
                        />
                      </label>
                      <button class="btn btn-primary" @click="userSearch" :disabled="loading">
                        <i class="fa fa-search" aria-hidden="true"></i> Search
                      </button>
                      <k-loader v-if="loading" />
                    </div>
                    <div class="list-container">
                      <k-table
                        v-if="searchedUsers.length > 0 && !selectedManager"
                        :headers="tableHeaders"
                        :rows="searchedUsers"
                        :panel="false"
                        :show-controls="false"
                        support-row-click
                        @row-clicked="selectedManager = $event"
                        class="line-manager-table"
                      />
                    </div>
                    <user-profile-card
                      v-if="selectedManager"
                      :name="selectedManager.full_name"
                      :email="selectedManager.email"
                      :avatarUrl="selectedManager.avatar_url"
                      :role="'Line Manager'"
                      :userId="selectedManager.user_id"
                    />
                  </div>
                </div>
                <div v-if="activeTab === 'create-new'">
                  <div class="create-new-manager">
                    <div class="new-line-manager-form">
                      <div class="new-line-manager-header">
                        <p>Create a new Line Manager and assign them to this learner on KATE.</p>
                        <p class="note-text">
                          Note this should only be used if the Line Manager you're looking for does not already have an
                          account on KATE - please check first. Note this will create a new account on KATE for the Line
                          Manager
                        </p>
                      </div>
                      <div>
                        <div class="form-group">
                          <div class="input-container">
                            <p>Full Name</p>
                            <input
                              type="text"
                              class="form-control"
                              v-model="newLineManagerName"
                              placeholder="Full Name"
                            />
                          </div>
                          <div class="input-container">
                            <p>Email</p>
                            <input
                              type="email"
                              class="form-control"
                              v-model="newLineManagerEmail"
                              placeholder="Email"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template #footer>
              <template v-if="activeTab === 'assign-existing'">
                <button
                  class="btn btn-danger"
                  @click="selectedManager = null"
                  :disabled="!selectedManager"
                  v-if="searchedUsers.length > 0"
                >
                  Clear Selection
                </button>
                <button
                  class="btn btn-success"
                  @click="assignNewLineManager"
                  :disabled="!selectedManager"
                  v-if="searchedUsers.length > 0"
                >
                  Confirm <i class="fas fa-check" aria-hidden="true"></i>
                </button>
              </template>
              <template v-if="activeTab === 'create-new'">
                <button class="btn btn-danger" @click="cancelEdit">Cancel</button>
                <button class="btn btn-success" @click="createLineManager" :disabled="loading || !validNewLineManagerForm">
                  Create & Assign <i class="fas fa-check" aria-hidden="true"></i>
                </button>
              </template>
            </template>
          </k-modal>
          <k-modal
            v-if="showRemoveModal"
            :show="showRemoveModal"
            @close="cancelRemove"
            class="remove-line-manager-modal"
          >
            <template #header>
              <h3>Remove Line Manager</h3>
            </template>
            <template #body>
              <p>Are you sure you want to remove the line manager?</p>
            </template>
            <template #footer>
              <button class="btn cancel-btn btn-outlined" @click="cancelRemove">Cancel</button>
              <button class="btn btn-danger" @click="removeLineManager" :disabled="loading">Remove <i class="fas fa-times" aria-hidden="true"></i></button>
            </template>
          </k-modal>
        </div>
    </template>
  </k-panel>
</template>

<style>
.update-line-manager-modal .modal-container {
  min-width: 70vw;
  min-height: 50vh;
}
</style>

<style scoped>
.line-manager-table {
  margin-bottom: 10px;
}

.line-manager-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.k-user-search-search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.k-user-search-search-bar label {
  flex-grow: 1;
}

.k-user-search-search-bar input {
  width: 100%;
}

.k-user-search-search-bar button {
  margin-left: 10px;
}

.list-container {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 10px;
}

.k-user-search-search-bar p {
  width: 100%;
  margin-bottom: 10px;
}

.new-line-manager-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 5px;
}

.note-text {
  font-weight: bold;
  font-style: italic;
  font-size: 0.9em;
}

.section-divider {
  margin: 2rem 0;
  border-color: var(--kate-grey-alt);
}

.new-line-manager-form {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.new-line-manager-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.new-line-manager-form input {
  width: 100%;
  margin-bottom: 5px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.form-actions > .btn-danger {
  margin-left: 10px;
}

.confirm-assignment {
  margin-bottom: 20px;
  text-align: right;
}

.confirm-assignment > .btn-success {
  margin-left: 10px;
}

.input-container {
  flex: 1;
}

.input-container p {
  margin-bottom: 0.5rem;
}

.form-actions-cancel {
  margin-top: 20px;
}

.user-table {
  width: 100%;
}

.user-row {
  cursor: pointer;
}

.user-row:hover {
  background-color: var(--kate-background-alt-alpha);
}

.modal-content-wrapper {
  display: flex;
  flex-direction: column;
}

.assign-existing-manager,
.create-new-manager {
  flex: 1;
}

.vertical-divider {
  border-left: 1px solid var(--kate-grey-alt);
}

.user-search-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.user-search-container label {
  flex-grow: 1;
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import KTextSearch from '@base-components/k-text-search.vue';
import KLoader from '@base-components/k-loader.vue';
import UserProfileCard from '@base-components/user-profile-card.vue';
import KModal from '@base-components/k-modal.vue';
import KNavTabs from '@base-components/k-nav-tabs.vue';

const EMAIL_REGEX = /.+@.+\..+/;

export default {
  components: {
    KPanel,
    KTable,
    KTextSearch,
    KLoader,
    UserProfileCard,
    KModal,
    KNavTabs,
  },
  props: {
    learnerId: {
      type: Number,
      required: true,
    },
    canUpdate: {
      type: Boolean,
      required: true,
    },
    lineManager: {
      type: Object,
      default: () => {},
    },
    programmeMemberId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      showContent: false,
      activeTab: 'assign-existing',
      tabs: [
        { key: 'assign-existing', name: 'Assign' },
        { key: 'create-new', name: 'Create' },
      ],
      showLineManager: false,
      showEditModal: false,
      showRemoveModal: false,
      filterText: '',
      searchedUsers: [],
      loading: false,
      newLineManagerName: '',
      newLineManagerEmail: '',
      selectedManager: null,
      tableHeaders: {
        user_id: { name: 'User ID' },
        full_name: { name: 'Name' },
        email: { name: 'Email' },
      },
    };
  },
  computed: {
    apiEndpoint() {
      return `/api/profile/line-managers/search?terms=${this.filterText.split(' ').join(',').toLowerCase()}`;
    },
    validNewLineManagerEmail() {
      return this.validateEmail(this.newLineManagerEmail);
    },
    validNewLineManagerName() {
      return this.validateName(this.newLineManagerName);
    },
    validNewLineManagerForm() {
      return this.validNewLineManagerEmail && this.validNewLineManagerName;
    },
  },
  methods: {
    userSearch() {
      this.loading = true;
      this.$http.get(this.apiEndpoint).then(res => {
        this.searchedUsers = res.data;
      }).catch(err => {
        this.$logger.error('Error searching for line managers', undefined, err);
      }).then(() => {
        this.loading = false;
      });
    },
    cancelEdit() {
      this.showEditModal = false;
      this.filterText = '';
      this.searchedUsers = [];
      this.newLineManagerName = '';
      this.newLineManagerEmail = '';
      this.selectedManager = null;
    },
    cancelRemove() {
      this.showRemoveModal = false;
    },
    createLineManager() {
      if (!this.validNewLineManagerForm) {
        this.$ktoast.showError('Please provide a valid name and email for the new line manager.');
        return;
      }
      this.loading = true;
      this.$Loading.start();
      const payload = {
        line_manager_name: this.newLineManagerName,
        line_manager_email: this.newLineManagerEmail,
      };
      this.$http.post(`/api/profile/admin/learner/${this.programmeMemberId}/line-manager`, payload).then(() => {
        this.$logger.info('Line manager created and assigned successfully');
        this.$emit('refresh-data');
        this.cancelEdit();
      }).catch(err => {
        this.$logger.error('Error creating line manager', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.loading = false;
        this.$Loading.finish();
      });
    },
    assignNewLineManager() {
      if (!this.selectedManager) {
        this.$ktoast.showError('Please select a line manager to assign.');
        return;
      }
      this.loading = true;
      this.$Loading.start();
      const newLineManagerId = this.selectedManager.user_id;
      this.$logger.info('Assigning new line manager to user', {
        studentId: this.learnerId,
        programmeMemberId: this.programmeMemberId,
        newLineManagerId,
      }, true);
      this.$http.put(`/api/profile/admin/learner/${this.programmeMemberId}/line-manager/${newLineManagerId}`).then(() => {
        this.$logger.info('Line manager successfully assigned', {
          studentId: this.learnerId,
          programmeMemberId: this.programmeMemberId,
          newLineManagerId,
        }, true);
        this.$emit('refresh-data');
        this.cancelEdit();
      }).catch(err => {
        if (this.$http.isWarning(err)) {
          this.$logger.warn('There was an error while attempting to assign new line manager', undefined, err);
        } else {
          this.$logger.error('There was an error while attempting to assign new line manager', undefined, err);
        }
        this.$ktoast.showError(err);
      }).then(() => {
        this.loading = false;
        this.$Loading.finish();
      });
    },
    removeLineManager() {
      this.loading = true;
      this.$Loading.start();
      this.$http.delete(`/api/profile/admin/programme-member/${this.programmeMemberId}/line-manager/${this.lineManager.user_id}`).then(() => {
        this.$logger.info('Line manager successfully removed', {
          studentId: this.learnerId,
          programmeMemberId: this.programmeMemberId,
          lineManagerId: this.lineManager.user_id,
        }, true);
        this.$ktoast.success('Line manager removed');
        this.$emit('refresh-data');
        this.cancelRemove();
      }).catch(err => {
        this.$logger.autowarn('There was an error while attempting to remove line manager', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.loading = false;
        this.$Loading.finish();
      });
    },
    validateRegex(name, regex) {
      return regex.test(name);
    },
    validateEmail(name) {
      return this.validateRegex(name, EMAIL_REGEX);
    },
    validateName(name) {
      return name.trim().length > 0;
    },
  },
};
</script>
